import { Paper as MuiPaper, PaperProps as MuiPaperProps } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CloseRounded as CloseRoundedIcon } from '@mui/icons-material';
import { IconButton } from '../button';

const theme = createTheme({
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: '2.8rem',
          border: 'solid 0.1rem var(--gray-50)',
          borderRadius: '0.6rem',
          boxShadow: 'none',

          backgroundColor: 'var(--white)',

          '&.MuiPaper-root': {
            overflowY: 'overlay',
          },

          '&[data-position=absolute]': {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: '70rem',
          },

          '&[data-size=none]': {
            width: '100%',
            height: '100%',
            maxHeight: 'initial',
            padding: 0,
            border: 'none',
            borderRadius: 0,
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
          '&[data-size=default]': {
            width: '54rem',
            paddingBottom: 'initial',

            '& > *:nth-of-type(1) > *:last-child': {
              paddingBottom: '2.8rem',
            },
          },
          '&[data-size=very-big]': {
            width: 'calc(100% - (var(--spacing-10) * 2))',
            maxWidth: '150rem',
            maxHeight: 'calc(100% - (var(--spacing-10) * 2))',
            paddingBottom: 'initial',

            '& > *:nth-of-type(1) > *:last-child': {
              paddingBottom: '2.8rem',
            },
          },
          '&[data-size=big]': {
            width: 'calc(100% - (var(--spacing-10) * 2))',
            maxWidth: '110rem',
            maxHeight: 'calc(100% - (var(--spacing-10) * 2))',
            paddingBottom: 'initial',

            '& > *:nth-of-type(1) > *:last-child': {
              paddingBottom: '2.8rem',
            },
          },
          '&[data-size=max-content]': {
            width: 'max-content',
          },
          '&[data-size=full-size]': {
            width: '100%',
            height: '100%',
          },
          '&[data-size=full-screen]': {
            width: '100%',
            height: '100%',

            position: 'fixed',
            borderRadius: 0,
            maxHeight: 'none',

            '& > div:nth-of-type(1)': {
              height: '100%',
            },
          },
          '&[data-size=mobile]': {
            width: '100%',
            height: '100%',
            maxHeight: 'initial',

            padding: 'var(--spacing-6)',
            paddingBottom: 0,
            borderRadius: 0,
            border: 'none',

            '& > div:nth-of-type(1)': {
              gridRowGap: 'var(--spacing-6)',
              height: '100%',
            },
            '& > div:nth-of-type(1) > *:last-child': {
              paddingBottom: 'var(--spacing-6)',
            },
          },
          '&[data-size=min-content]': {
            maxWidth: 'calc(100% - (var(--spacing-6) * 2))',
            width: 'max-content',
            padding: 'var(--spacing-6)',
            paddingBottom: 0,

            '& > div:nth-of-type(1)': {
              gridRowGap: 'var(--spacing-6)',
              height: '100%',
            },
            '& > div:nth-of-type(1) > *:last-child': {
              paddingBottom: 'var(--spacing-6)',
            },
          },

          '& .MuiCloseIcon': {
            position: 'absolute',
            top: '0.4rem',
            right: '0.4rem',
          },
        },
      },
    },
  },
});

export type PaperSizeProps =
  | 'none'
  | 'default'
  | 'very-big'
  | 'big'
  | 'max-content'
  | 'full-size'
  | 'full-screen'
  | 'mobile'
  | 'min-content';

export interface PaperProps extends MuiPaperProps {
  paperSize?: PaperSizeProps;
  position?: 'absolute' | 'initial';
  onClose?: () => void;
}

export const Paper: React.FC<PaperProps> = ({
  paperSize = 'default',
  position = 'initial',
  children,
  onClose,
  ...props
}: PaperProps) => {
  return (
    <ThemeProvider theme={theme}>
      <MuiPaper data-size={paperSize} data-position={position} {...props}>
        {children}
        {onClose && (
          <IconButton className={'MuiCloseIcon'} onClick={onClose} iconColor={'--gray-70'}>
            <CloseRoundedIcon />
          </IconButton>
        )}
      </MuiPaper>
    </ThemeProvider>
  );
};
